/**
 * Pre-run configuration modal.
 *
 * Shows any combination of: environment picker, scenario picker, input values.
 * Sections are hidden when the workflow has nothing to configure for them.
 *
 * @module FlowDesigner/components/WorkflowRunConfigModal
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  Select,
  Switch,
  Text,
  TextArea,
  TextField,
} from '@chatsdet/ui';
import { SuperadminOnly } from '../../../components/SuperadminOnly';
import type {
  WorkflowCredentialInputDefinition,
  EnvironmentStage,
  WorkflowInputDefinition,
  WorkflowScenario,
  WorkflowValueInputDefinition,
} from '@chatsdet/types';
import {
  DEFAULT_BROWSER_ENGINE,
  DEFAULT_BROWSER_VIEWPORT_PRESET,
  isBrowserEngine,
  isBrowserViewportPreset,
  type BrowserEngine,
  type BrowserViewportPreset,
} from '@chatsdet/types';
import type { CredentialTemplate } from '../../../../hooks/useCredentialTemplates';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';
import { ViewportPicker } from '../../../components/ViewportPicker';
import { BrowserEnginePicker } from '../../../components/BrowserEnginePicker';
import {
  pickInitialEnvironmentId,
  pickInitialEnvironmentStageId,
  type EnvironmentOption,
} from './environmentSelection';

/** Readiness of the sandbox environment's backing instance (when one exists). */
interface SandboxEnvironmentReadiness {
  environmentId: string;
  status: 'asleep' | 'warming' | 'ready' | 'stopping' | 'failed';
}

interface WorkflowRunConfigModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  inputs?: WorkflowInputDefinition[];
  scenarios?: WorkflowScenario[];
  environments?: EnvironmentOption[];
  isCrossApplication?: boolean;
  eligibleEnvironmentIds?: string[];
  /**
   * Env to pre-select, derived server-side from the login credential's binding
   * (e.g. a Sales-bound credential → the Sales env). Takes precedence over the
   * property default when eligible. Null/undefined ⇒ fall back to the default.
   */
  preferredEnvironmentId?: string | null;
  /** Saved workflow viewport (or the previewed history viewport); null uses org default. */
  workflowViewportSize: BrowserViewportPreset | null;
  /** When the property has a sandbox env, its current readiness — gates Run. */
  sandboxReadiness?: SandboxEnvironmentReadiness | null;
  credentialTemplates?: CredentialTemplate[];
  onConfirm: (args: {
    values?: Record<string, string>;
    scenarioId?: string;
    environmentId?: string;
    environmentStageId?: string;
    browserEngine?: BrowserEngine;
    viewportSize?: BrowserViewportPreset;
    forceSingleAgentSegment?: boolean;
  }) => void;
}

const NO_SCENARIO_KEY = '__none__';

function pickInitialScenarioId(scenarios?: WorkflowScenario[]): string | undefined {
  if (!scenarios || scenarios.length === 0) return undefined;
  return scenarios.find((s) => s.isDefault)?.id ?? scenarios[0]!.id;
}

function defaultsFromInputs(inputs: WorkflowInputDefinition[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const input of inputs) {
    if (input.kind === 'value') out[input.name] = input.defaultValue ?? '';
    if (input.kind === 'credential' && input.defaultCredentialTemplateId) {
      out[`${input.name}.credentialTemplateId`] = input.defaultCredentialTemplateId;
    }
  }
  return out;
}

/**
 * Values submitted verbatim would otherwise send `''` for a value-kind input
 * with no declared default that the user never touched — which arrives at
 * resolveWorkflowInputDefaults as an *override*, always wins over "skip", and
 * silently shadows a seed-extracted value for the same variable name
 * (production incident: run 50eb849f, `lp_project_name` came back blank).
 * Omit those keys so "no default, not filled in" round-trips as "not
 * supplied" rather than "explicitly blank". Inputs with a declared default,
 * or with a non-empty current value, are submitted unchanged.
 */
export function submittableValues(
  values: Record<string, string>,
  inputs: WorkflowInputDefinition[]
): Record<string, string> {
  const out = { ...values };
  for (const input of inputs) {
    if (
      input.kind === 'value' &&
      input.defaultValue === undefined &&
      (out[input.name] ?? '') === ''
    ) {
      delete out[input.name];
    }
  }
  return out;
}

function mergeScenarioValues(
  base: Record<string, string>,
  scenarioValues: Record<string, string> | undefined
): Record<string, string> {
  if (!scenarioValues) return base;
  const out = { ...base };
  for (const [key, value] of Object.entries(scenarioValues)) {
    if (typeof value === 'string') out[key] = value;
  }
  return out;
}

function scenarioToFlatValues(
  scenario: WorkflowScenario | undefined
): Record<string, string> | undefined {
  if (!scenario) return undefined;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(scenario.values ?? {})) {
    if (typeof value === 'string') out[key] = value;
  }
  for (const [key, fields] of Object.entries(scenario.credentials ?? {})) {
    if (fields.credentialTemplateId) {
      out[`${key}.credentialTemplateId`] = fields.credentialTemplateId;
    }
  }
  return out;
}

export const WorkflowRunConfigModal: React.FC<WorkflowRunConfigModalProps> = ({
  isOpen,
  onOpenChange,
  inputs,
  scenarios,
  environments,
  isCrossApplication = false,
  eligibleEnvironmentIds,
  preferredEnvironmentId,
  workflowViewportSize,
  sandboxReadiness,
  credentialTemplates,
  onConfirm,
}) => {
  const stagesQ = useQuery({
    queryKey: ['environment-kinds'],
    queryFn: async (): Promise<{ stages: EnvironmentStage[] }> => {
      const response = await fetch(`${API_URL}/org/environment-kinds`, {
        credentials: 'include',
      });
      if (!response.ok) throw await toApiError(response, 'Failed to fetch stages');
      return response.json();
    },
    enabled: isCrossApplication,
    staleTime: 5 * 60 * 1000,
  });
  const stages = stagesQ.data?.stages ?? [];
  const valueInputs = React.useMemo(
    () => (inputs ?? []).filter((i): i is WorkflowValueInputDefinition => i.kind === 'value'),
    [inputs]
  );
  const credentialInputs = React.useMemo(
    () =>
      (inputs ?? []).filter((i): i is WorkflowCredentialInputDefinition => i.kind === 'credential'),
    [inputs]
  );
  const hasScenarios = !!scenarios && scenarios.length > 0;
  const hasValueInputs = valueInputs.length > 0 || credentialInputs.length > 0;
  const selectableEnvironments = React.useMemo(
    () =>
      isCrossApplication
        ? environments?.filter(
            (environment) =>
              !!environment.environmentKindId &&
              environment.type !== 'ephemeral' &&
              environment.type !== 'sandbox'
          )
        : environments,
    [environments, isCrossApplication]
  );
  const hasMultipleEnvs = isCrossApplication
    ? stages.length > 0
    : (selectableEnvironments?.length ?? 0) >= 2;

  const [scenarioId, setScenarioId] = React.useState<string | undefined>(() =>
    pickInitialScenarioId(scenarios)
  );
  const [values, setValues] = React.useState<Record<string, string>>(() => {
    const base = defaultsFromInputs(inputs ?? []);
    const initial = pickInitialScenarioId(scenarios);
    const selected = scenarios?.find((s) => s.id === initial);
    return mergeScenarioValues(base, scenarioToFlatValues(selected));
  });
  const [environmentId, setEnvironmentId] = React.useState<string | undefined>(() =>
    isCrossApplication
      ? pickInitialEnvironmentStageId(environments, stages, preferredEnvironmentId)
      : pickInitialEnvironmentId(
          selectableEnvironments,
          eligibleEnvironmentIds,
          preferredEnvironmentId
        )
  );
  const [viewportSize, setViewportSize] = React.useState<BrowserViewportPreset>(
    DEFAULT_BROWSER_VIEWPORT_PRESET
  );
  const [browserEngine, setBrowserEngine] = React.useState<BrowserEngine>(DEFAULT_BROWSER_ENGINE);
  const [forceSingleAgentSegment, setForceSingleAgentSegment] = React.useState(false);
  // True once the user picks a viewport this open — stops the org-default seed
  // from clobbering their choice if the settings query resolves late.
  const viewportTouchedRef = React.useRef(false);
  const browserEngineTouchedRef = React.useRef(false);

  // The org default viewport, used to seed the picker before any override.
  const orgViewportQuery = useQuery({
    queryKey: ['org-agent-browser-settings'],
    queryFn: async (): Promise<{
      data?: { defaultBrowserEngine?: string; defaultBrowserViewport?: string };
    }> => {
      const response = await fetch(`${API_URL}/org/settings`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to load agent browser settings');
      return response.json();
    },
  });
  const orgViewportValue = orgViewportQuery.data?.data?.defaultBrowserViewport;
  const orgDefaultViewport: BrowserViewportPreset = isBrowserViewportPreset(orgViewportValue)
    ? orgViewportValue
    : DEFAULT_BROWSER_VIEWPORT_PRESET;
  const initialViewport = isBrowserViewportPreset(workflowViewportSize)
    ? workflowViewportSize
    : orgDefaultViewport;
  const orgBrowserEngineValue = orgViewportQuery.data?.data?.defaultBrowserEngine;
  const orgDefaultBrowserEngine: BrowserEngine = isBrowserEngine(orgBrowserEngineValue)
    ? orgBrowserEngineValue
    : DEFAULT_BROWSER_ENGINE;

  // Reset state when the modal opens.
  React.useEffect(() => {
    if (!isOpen) return;
    const initialScenarioId = pickInitialScenarioId(scenarios);
    const selected = scenarios?.find((s) => s.id === initialScenarioId);
    setScenarioId(initialScenarioId);
    setValues(
      mergeScenarioValues(defaultsFromInputs(inputs ?? []), scenarioToFlatValues(selected))
    );
    setEnvironmentId(
      isCrossApplication
        ? pickInitialEnvironmentStageId(environments, stages, preferredEnvironmentId)
        : pickInitialEnvironmentId(
            selectableEnvironments,
            eligibleEnvironmentIds,
            preferredEnvironmentId
          )
    );
    viewportTouchedRef.current = false;
    browserEngineTouchedRef.current = false;
    setBrowserEngine(orgDefaultBrowserEngine);
    setViewportSize(initialViewport);
    setForceSingleAgentSegment(false);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // The stage query may resolve after the modal opens. Seed once without
  // overwriting a stage the user has already chosen.
  React.useEffect(() => {
    if (!isOpen || !isCrossApplication || environmentId) return;
    setEnvironmentId(pickInitialEnvironmentStageId(environments, stages, preferredEnvironmentId));
  }, [environmentId, environments, isCrossApplication, isOpen, preferredEnvironmentId, stages]);

  // Seed from the workflow setting (or org default) once its inputs resolve,
  // unless the user already changed it this open.
  React.useEffect(() => {
    if (!isOpen || viewportTouchedRef.current) return;
    setViewportSize(initialViewport);
  }, [isOpen, initialViewport]);

  React.useEffect(() => {
    if (!isOpen || browserEngineTouchedRef.current) return;
    setBrowserEngine(orgDefaultBrowserEngine);
  }, [isOpen, orgDefaultBrowserEngine]);

  const handleViewportChange = (next: BrowserViewportPreset) => {
    viewportTouchedRef.current = true;
    setViewportSize(next);
  };

  const handleBrowserEngineChange = (next: BrowserEngine) => {
    browserEngineTouchedRef.current = true;
    setBrowserEngine(next);
  };

  const handleScenarioChange = (key: string) => {
    if (key === NO_SCENARIO_KEY) {
      setScenarioId(undefined);
      setValues(defaultsFromInputs(inputs ?? []));
      return;
    }
    const selected = scenarios?.find((s) => s.id === key);
    setScenarioId(selected?.id);
    setValues(
      mergeScenarioValues(defaultsFromInputs(inputs ?? []), scenarioToFlatValues(selected))
    );
  };

  const handleConfirm = () => {
    onConfirm({
      values: hasValueInputs ? submittableValues(values, inputs ?? []) : undefined,
      scenarioId,
      environmentId: hasMultipleEnvs && !isCrossApplication ? environmentId : undefined,
      environmentStageId: hasMultipleEnvs && isCrossApplication ? environmentId : undefined,
      browserEngine,
      viewportSize,
      forceSingleAgentSegment: forceSingleAgentSegment || undefined,
    });
    onOpenChange(false);
  };

  // Selected env resolves to the sandbox env while its backing instance is
  // not ready yet → block submit with an inline warming note. (A run started
  // anyway would still work — the run job blocks on ensure server-side — but
  // surfacing the wait here beats a silently slow run.)
  const effectiveEnvironmentId =
    environmentId ??
    pickInitialEnvironmentId(
      selectableEnvironments,
      eligibleEnvironmentIds,
      preferredEnvironmentId
    );
  const sandboxBlocked =
    !!sandboxReadiness &&
    sandboxReadiness.environmentId === effectiveEnvironmentId &&
    sandboxReadiness.status !== 'ready';

  const isValid =
    !sandboxBlocked &&
    valueInputs.every((input) => !input.required || (values[input.name] ?? '').trim().length > 0) &&
    credentialInputs.every(
      (input) =>
        !input.required || (values[`${input.name}.credentialTemplateId`] ?? '').trim().length > 0
    );

  const renderField = (input: WorkflowValueInputDefinition) => {
    const label = input.required ? `${input.label} *` : input.label;

    if (input.type === 'textarea') {
      return (
        <TextArea
          label={label}
          description={input.description}
          placeholder={input.placeholder}
          value={values[input.name] ?? ''}
          onChange={(v) => setValues((prev) => ({ ...prev, [input.name]: v }))}
        />
      );
    }

    if (input.type === 'select' && input.options) {
      const selectItems = input.options.map((opt) => ({
        id: opt.value,
        label: opt.label,
      }));
      return (
        <Select
          label={label}
          items={selectItems}
          placeholder="Select..."
          selectedKey={values[input.name] || null}
          onSelectionChange={(key) =>
            setValues((prev) => ({ ...prev, [input.name]: key as string }))
          }
        />
      );
    }

    return (
      <TextField
        label={label}
        description={input.description}
        placeholder={input.placeholder}
        type={input.type === 'password' ? 'password' : input.type === 'email' ? 'email' : 'text'}
        value={values[input.name] ?? ''}
        onChange={(v) => setValues((prev) => ({ ...prev, [input.name]: v }))}
        errorMessage={input.required && !(values[input.name] ?? '').trim() ? 'Required' : undefined}
      />
    );
  };

  const templateItems = React.useMemo(
    () =>
      (credentialTemplates ?? []).map((template) => ({ id: template.id, label: template.name })),
    [credentialTemplates]
  );

  const renderCredentialField = (input: WorkflowCredentialInputDefinition) => (
    <div key={input.name}>
      <Select
        label={input.required ? `${input.label} *` : input.label}
        items={templateItems}
        placeholder="Select persona..."
        selectedKey={values[`${input.name}.credentialTemplateId`] || null}
        onSelectionChange={(key) =>
          setValues((prev) => ({ ...prev, [`${input.name}.credentialTemplateId`]: key as string }))
        }
      />
      <Text size="xs" className="text-muted-foreground/60 font-mono mt-0.5">
        $var.{input.name}.credentialTemplateId
      </Text>
    </div>
  );

  const scenarioItems = React.useMemo(() => {
    if (!scenarios) return [];
    return [
      { id: NO_SCENARIO_KEY, label: '— Manual input —' },
      ...scenarios.map((s) => ({
        id: s.id,
        label: s.isDefault ? `${s.name} (default)` : s.name,
      })),
    ];
  }, [scenarios]);

  const environmentItems = React.useMemo(() => {
    if (isCrossApplication) {
      return stages.map((stage) => ({ id: stage.id, label: capitalize(stage.name) }));
    }
    if (!selectableEnvironments) return [];
    const eligible = new Set(
      eligibleEnvironmentIds ?? selectableEnvironments.map((environment) => environment.id)
    );
    return selectableEnvironments.map((e) => {
      const isEligible = eligible.has(e.id);
      const suffix = e.isDefault ? ' (default)' : '';
      return {
        id: e.id,
        label: isEligible
          ? `${isCrossApplication ? capitalize(e.environmentKindName ?? e.name) : e.name}${suffix}`
          : `${isCrossApplication ? capitalize(e.environmentKindName ?? e.name) : e.name}${suffix} — not eligible`,
        disabled: !isEligible,
      };
    });
  }, [selectableEnvironments, eligibleEnvironmentIds, isCrossApplication, stages]);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      {/* Hidden trigger — modal is controlled externally */}
      <span className="hidden" />
      <Dialog
        title="Run configuration"
        description="Override the browser engine, viewport, environment, and/or inputs for this run."
      >
        <DialogContent>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Text size="sm" weight="medium">
                Browser engine
              </Text>
              <BrowserEnginePicker
                value={browserEngine}
                onChange={handleBrowserEngineChange}
                aria-label="Browser engine"
              />
              <Text size="xs" className="text-muted-foreground">
                The browser engine the agent runs with. Defaults to your organization&apos;s
                setting.
              </Text>
            </div>
            <div className="space-y-1.5">
              <Text size="sm" weight="medium">
                Viewport size
              </Text>
              <ViewportPicker
                value={viewportSize}
                onChange={handleViewportChange}
                aria-label="Viewport size for this run"
              />
              <Text size="xs" className="text-muted-foreground">
                The browser size the agent runs at for this run. Defaults to your
                organization&apos;s setting.
              </Text>
            </div>
            <SuperadminOnly>
              <div className="space-y-1">
                <Switch
                  isSelected={forceSingleAgentSegment}
                  onChange={setForceSingleAgentSegment}
                  label="Run via single-agent segment"
                />
                <Text size="xs" className="text-muted-foreground">
                  Forces this run onto the single-agent segment execution path, bypassing the org
                  feature flag. For validating the path before rollout.
                </Text>
              </div>
            </SuperadminOnly>
            {hasMultipleEnvs && (
              <div className="space-y-1">
                <Select
                  label={isCrossApplication ? 'Stage' : 'Environment'}
                  items={environmentItems}
                  selectedKey={environmentId ?? null}
                  onSelectionChange={(key) => setEnvironmentId(key as string)}
                />
                <Text size="xs" className="text-muted-foreground">
                  {isCrossApplication
                    ? 'Choose the named stage to map across every application in this flow.'
                    : 'Choose which environment this run should target.'}
                </Text>
              </div>
            )}
            {hasScenarios && (
              <div className="space-y-1">
                <Select
                  label="Scenario"
                  items={scenarioItems}
                  selectedKey={scenarioId ?? NO_SCENARIO_KEY}
                  onSelectionChange={(key) => handleScenarioChange(key as string)}
                />
                <Text size="xs" className="text-muted-foreground">
                  Pick a saved scenario to pre-fill values. You can still edit any field below.
                </Text>
              </div>
            )}
            {valueInputs.map((input) => (
              <div key={input.name}>
                {renderField(input)}
                <Text size="xs" className="text-muted-foreground/60 font-mono mt-0.5">
                  $var.{input.name}
                </Text>
              </div>
            ))}
            {credentialInputs.map(renderCredentialField)}
          </div>
        </DialogContent>
        <DialogFooter>
          <Flex gap="sm" justify="end" align="center">
            {sandboxBlocked && (
              <Text size="xs" className="text-muted-foreground">
                {sandboxReadiness?.status === 'failed'
                  ? 'Sandbox failed to start — retry from the sandbox chip.'
                  : 'Sandbox warming up — Run unlocks when it’s ready.'}
              </Text>
            )}
            <Button variant="outline" onPress={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="primary" onPress={handleConfirm} isDisabled={!isValid}>
              Run
            </Button>
          </Flex>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
};

function capitalize(value: string): string {
  return value.length === 0 ? value : value[0].toUpperCase() + value.slice(1);
}
